import { mobileApiRequest } from '../../apiRequests'
import { getWorkspaceID } from '../../../utils'

// =============================================================================
// External Tables API Functions
// Per EXTERNAL_TABLE_FRONTEND_GUIDE.md patterns
// =============================================================================

/**
 * Get all external tables for the current workspace
 * External tables are represented by ReportInbox objects with inboxType='external'
 */
export const getExternalTables = async () => {
    const workspaceID = getWorkspaceID()
    const action = {
        action: 'getObjects',
        class: 'scoop.ingest.ReportInbox',
        attributeName: 'workspaceID',
        attributeValue: workspaceID,
    }
    const response = await mobileApiRequest(action)
    return response?.objects?.filter((inbox) => inbox.inboxType === 'external')
}

/**
 * Create a new external table
 * Creates both a ReportInbox (entry point) and ExternalReportSeriesTable (configuration)
 *
 * Per EXTERNAL_TABLE_FRONTEND_GUIDE.md:
 * - ReportInbox key is inboxName (not inboxID)
 * - ReportSeriesTable class is scoop.reportseriestable.ReportSeriesTable
 * - Must include metadata.tableType: "External"
 * - Must include externalClusterId: "inbox_<INBOX_ID>"
 * - ALWAYS use updateObject (supports upsert, putObject is dangerous)
 */
export const createExternalTable = async (externalTableData) => {
    const workspaceID = getWorkspaceID()

    const { inbox, table } = externalTableData

    // Step 1: Create ReportInbox
    // Per EXTERNAL_TABLE_FRONTEND_GUIDE.md: ALWAYS use updateObject (supports upsert)
    // putObject is dangerous - it deletes and re-inserts, causing data loss
    const inboxAction = {
        action: 'updateObject',
        class: 'scoop.ingest.ReportInbox',
        key: inbox.inboxName,
        updates: {
            inboxName: inbox.inboxName,
            inboxID: inbox.inboxID,
            workspaceID,
            inboxType: 'external',
            label: inbox.label,
            description: inbox.description,
            extractDefinition: inbox.extractDefinition,
        },
    }

    const inboxResult = await mobileApiRequest(inboxAction)

    // Step 2: Create ExternalReportSeriesTable
    // Class is ReportSeriesTable (not ExternalReportSeriesTable)
    // Must include metadata.tableType: "External" and externalClusterId: "inbox_<INBOX_ID>"
    // Per EXTERNAL_TABLE_FRONTEND_GUIDE.md: ALWAYS use updateObject (supports upsert)
    const tableAction = {
        action: 'updateObject',
        class: 'scoop.reportseriestable.ReportSeriesTable',
        key: table.tableName,
        updates: {
            tableName: table.tableName,
            inboxID: inbox.inboxID,
            metadata: {
                tableType: 'External',
            },
            externalClusterId: `inbox_${inbox.inboxID}`,
            externalSchema: table.externalSchema,
            externalTableName: table.externalTableName,
            columnMetadata: table.columnMetadata,
            joinRelationships: table.joinRelationships || [],
            queryTimeoutSeconds: table.queryTimeoutSeconds || 120,
        },
    }

    const tableResult = await mobileApiRequest(tableAction)

    return { inbox: inboxResult, table: tableResult }
}

/**
 * Delete an external table
 * Deletes both the ReportInbox and associated ExternalReportSeriesTable
 *
 * Per EXTERNAL_TABLE_FRONTEND_GUIDE.md:
 * - ReportInbox key is inboxName
 * - ReportSeriesTable key is tableName
 *
 * @param {Object} params
 * @param {string} params.inboxName - The inboxName (key for ReportInbox)
 * @param {string} params.tableName - The tableName (key for ReportSeriesTable)
 */
export const deleteExternalTable = async ({ inboxName, tableName }) => {
    const workspaceID = getWorkspaceID()

    // Delete ReportInbox (key is inboxName per documentation)
    const deleteInboxAction = {
        action: 'deleteObject',
        class: 'scoop.ingest.ReportInbox',
        workspaceID,
        key: inboxName,
    }

    await mobileApiRequest(deleteInboxAction)

    // Delete associated ExternalReportSeriesTable (key is tableName)
    if (tableName) {
        const deleteTableAction = {
            action: 'deleteObject',
            class: 'scoop.reportseriestable.ReportSeriesTable',
            workspaceID,
            key: tableName,
        }

        await mobileApiRequest(deleteTableAction)
    }

    return { success: true }
}

// =============================================================================
// Schema Discovery API - IMPLEMENTED
// See EXTERNAL_TABLE_FRONTEND_GUIDE.md for documentation
// =============================================================================

/**
 * Browse schemas in an external database
 * @param {Object} connectionDetails - Connection credentials
 * @returns {Promise<{schemas: Array<{schemaName: string, isDefault: boolean}>}>}
 */
export const listSchemas = async (connectionDetails) => {
    const action = {
        action: 'browseExternalSchema',
        operation: 'listSchemas',
        url: connectionDetails.jdbcUrl,
        username: connectionDetails.username,
        password: connectionDetails.password,
        driverClass: connectionDetails.driverClass,
    }

    return await mobileApiRequest(action)
}

/**
 * Browse tables in a schema
 * @param {Object} connectionDetails - Connection credentials
 * @param {string} schema - Schema name to browse
 * @returns {Promise<{tables: Array<{tableName: string, tableType: string, rowCount: number|null}>}>}
 */
export const listTables = async (connectionDetails, schema) => {
    const action = {
        action: 'browseExternalSchema',
        operation: 'listTables',
        url: connectionDetails.jdbcUrl,
        username: connectionDetails.username,
        password: connectionDetails.password,
        driverClass: connectionDetails.driverClass,
        schema,
    }

    return await mobileApiRequest(action)
}

/**
 * Browse columns in a table
 * @param {Object} connectionDetails - Connection credentials
 * @param {string} schema - Schema name
 * @param {string} tableName - Table name
 * @returns {Promise<{columns: Array<{columnName: string, columnType: string, isPrimaryKey: boolean, isForeignKey: boolean}>}>}
 */
export const listColumns = async (connectionDetails, schema, tableName) => {
    const action = {
        action: 'browseExternalSchema',
        operation: 'listColumns',
        url: connectionDetails.jdbcUrl,
        username: connectionDetails.username,
        password: connectionDetails.password,
        driverClass: connectionDetails.driverClass,
        schema,
        tableName,
    }

    return await mobileApiRequest(action)
}

/**
 * Get sample data from a table
 * @param {Object} connectionDetails - Connection credentials
 * @param {string} schema - Schema name
 * @param {string} tableName - Table name
 * @param {number} limit - Number of rows to fetch (default: 10, max: 100)
 * @returns {Promise<{columns: Array, rows: Array<Array>, totalRows: number}>}
 */
export const getSampleData = async (connectionDetails, schema, tableName, limit = 10) => {
    const action = {
        action: 'browseExternalSchema',
        operation: 'sampleData',
        url: connectionDetails.jdbcUrl,
        username: connectionDetails.username,
        password: connectionDetails.password,
        driverClass: connectionDetails.driverClass,
        schema,
        tableName,
        limit,
    }

    return await mobileApiRequest(action)
}

/**
 * Generate a new unique ID for ReportInbox
 * Per EXTERNAL_TABLE_FRONTEND_GUIDE.md: Use getNewID action
 */
export const getNewID = async () => {
    const action = {
        action: 'getNewID',
        class: 'scoop.ingest.ReportInbox',
    }

    const response = await mobileApiRequest(action)
    return response?.newID
}

/**
 * Test an external database connection
 */
export const testExternalConnection = async (connectionConfig) => {
    const action = {
        action: 'testJDBC',
        driverClass: connectionConfig.driverClass,
        server: connectionConfig.serverHost,
        url: connectionConfig.url || null,
        port: connectionConfig.port,
        database: connectionConfig.database,
        driver: connectionConfig.driver,
        username: connectionConfig.username,
        password: connectionConfig.password,
    }

    const response = await mobileApiRequest(action)
    return response?.result || response
}

// =============================================================================
// External Table Creation API - Star Schema Support
// Creates dimensions FIRST, then fact table with joinRelationships
// =============================================================================

/**
 * Generate short UUID for inboxName uniqueness
 */
const generateUUID = () => crypto.randomUUID().split('-')[0]

/**
 * Build extractDefinition from connection details
 * @param {Object} connection - Connection configuration from wizard
 */
const buildExtractDefinition = (connection) => ({
    connectorType: connection.driver?.toLowerCase(),
    object: {
        connectorDefinitionFields: [
            { name: 'jdbcUrl', value: connection.jdbcUrl },
            { name: 'username', value: connection.username },
            { name: 'password', value: connection.password },
        ],
    },
})

/**
 * Generate inboxID in the format: I_{TYPE}_{TABLE}_{newID}
 * @param {string} type - 'DIM' or 'FACT'
 * @param {string} tableName - External table name
 * @param {number} newID - Generated ID from getNewID
 */
const formatInboxID = (type, tableName, newID) => {
    const shortName = tableName
        .substring(0, 10)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
    return `I_${type}_${shortName}_${newID}`
}

/**
 * Create a dimension table (ReportInbox + ExternalReportSeriesTable)
 *
 * @param {Object} dimension - Dimension data from wizard
 * @param {string} inboxID - Generated inboxID
 * @param {Object} wizardData - Full wizard data (for connection)
 * @returns {Promise<{inboxID: string, inboxName: string, tableName: string}>}
 */
export const createDimensionTable = async (dimension, inboxID, wizardData) => {
    const workspaceID = getWorkspaceID()
    const { connection } = wizardData

    const label = dimension.label || dimension.externalTableName
    const inboxName = `${label}_${generateUUID()}`
    const tableName = `RST_DIM_${dimension.externalTableName}`

    // Step 1: Create ReportInbox
    await mobileApiRequest({
        action: 'updateObject',
        class: 'scoop.ingest.ReportInbox',
        key: inboxName,
        updates: {
            inboxName,
            inboxID,
            workspaceID,
            inboxType: 'external',
            label,
            extractDefinition: buildExtractDefinition(connection),
        },
    })

    // Step 2: Create ExternalReportSeriesTable
    await mobileApiRequest({
        action: 'updateObject',
        class: 'scoop.reportseriestable.ReportSeriesTable',
        key: tableName,
        updates: {
            tableName,
            inboxID,
            metadata: { tableType: 'External' },
            externalClusterId: `inbox_${inboxID}`,
            externalSchema: dimension.schema,
            externalTableName: dimension.externalTableName,
            columnMetadata: dimension.columns
                .filter((c) => c.selected)
                .map((c) => ({
                    columnName: c.columnName,
                    columnType: c.columnType,
                })),
        },
    })

    return { inboxID, inboxName, tableName }
}

/**
 * Create a fact table (ReportInbox + ExternalReportSeriesTable with joinRelationships)
 *
 * @param {Object} factTable - Fact table data from wizard
 * @param {string} inboxID - Generated inboxID
 * @param {Object} wizardData - Full wizard data (for connection, metadata)
 * @param {Object} dimensionRSTMap - Map of dimension.tableName → RST tableName
 * @returns {Promise<{inboxID: string, inboxName: string, tableName: string}>}
 */
export const createFactTable = async (factTable, inboxID, wizardData, dimensionRSTMap) => {
    const workspaceID = getWorkspaceID()
    const { connection, metadata } = wizardData

    const label = metadata.name
    const inboxName = `${label}_${generateUUID()}`
    const tableName = `RST_FACT_${factTable.externalTableName}`

    // Step 1: Create ReportInbox
    await mobileApiRequest({
        action: 'updateObject',
        class: 'scoop.ingest.ReportInbox',
        key: inboxName,
        updates: {
            inboxName,
            inboxID,
            workspaceID,
            inboxType: 'external',
            label,
            description: metadata.description,
            extractDefinition: buildExtractDefinition(connection),
        },
    })

    // Step 2: Map join relationships to use RST tableNames
    const joinRelationships = (factTable.joinRelationships || []).map((jr) => ({
        dimensionTableID: dimensionRSTMap[jr.dimensionTableID] || jr.dimensionTableID,
        sourceColumn: jr.sourceColumn,
        targetColumn: jr.targetColumn,
        tableAlias: jr.tableAlias,
        joinType: jr.joinType || 'LEFT',
    }))

    // Step 3: Create ExternalReportSeriesTable
    await mobileApiRequest({
        action: 'updateObject',
        class: 'scoop.reportseriestable.ReportSeriesTable',
        key: tableName,
        updates: {
            tableName,
            inboxID,
            metadata: { tableType: 'External' },
            externalClusterId: `inbox_${inboxID}`,
            externalSchema: factTable.schema,
            externalTableName: factTable.externalTableName,
            columnMetadata: factTable.columns
                .filter((c) => c.selected)
                .map((c) => ({
                    columnName: c.columnName,
                    columnType: c.columnType,
                })),
            joinRelationships,
        },
    })

    return { inboxID, inboxName, tableName }
}

/**
 * Generate all IDs needed for the external table creation
 * Calls getNewID for each dimension + fact table
 *
 * @param {number} dimensionCount - Number of dimensions
 * @returns {Promise<{dimensionIDs: string[], factID: string}>}
 */
export const generateAllIDs = async (dimensionCount, dimensions, factTableName) => {
    const dimensionIDs = []

    // Generate IDs for dimensions
    for (let i = 0; i < dimensionCount; i++) {
        const newID = await getNewID()
        const dim = dimensions[i]
        dimensionIDs.push(formatInboxID('DIM', dim.externalTableName, newID))
    }

    // Generate ID for fact table
    const factNewID = await getNewID()
    const factID = formatInboxID('FACT', factTableName, factNewID)

    return { dimensionIDs, factID }
}
